import { ArrowDropDown, Close } from "@mui/icons-material";
import {
  Box,
  Divider,
  Drawer,
  MenuItem,
  Select,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import { CircleButton } from "components/CircleButton";
import { StyledDrawer } from "components/SearchDrawer/styled";
import { ReactComponent as SendIcon } from "assets/icons/portfolio-send.svg";
import { Value } from "components/Value";
import { Chain, CHAIN_CONFIG, NATIVE_CURRENCY_CHECK_LIST } from "chainConfigs";
import { Card } from "components/Base/Containers";
import { BaseButton, TextButton } from "components/Base/Buttons";
import CoinAvatar, { NATIVE_TOKEN_MAP } from "components/CoinAvatar";
import { SmallNumberView } from "components/SmallValueView/SmallValueView";
import { StyledInputV2 } from "components/portfolio/styled";
import { TON } from "consts";
import { DarkDrawer } from "components/TradeDrawer";
import { minimumGasBufferUSD, MODES } from "tradeConfigs";
import { Col, Row } from "v2/components/base/Flex/ui";
import CabalTownLogo from "assets/icons/cabal-town-logo-v2.png";
import BasicButton from "v2/components/Buttons/BasicButton";
import { Input } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import CloseButton from "v2/components/Buttons/CloseButton";
import { ConfirmDrawer } from "components/CrossChainSwapDrawerV2/ConfirmDrawer";
const StyledSelect = styled(Select)<{ noPointer?: boolean }>(({ theme, noPointer }) => ({
  pointerEvents: noPointer ? "none" : "auto",
  cursor: "pointer",
  borderRadius: `${theme.spacing(1.5)} 0 0 ${theme.spacing(1.5)}`,
  overflow: "hidden",

  "& :focus-visible": {
    backgroundColor: "transparent",
    border: "none",
    outline: "none",
  },

  ".MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    borderRadius: `${theme.spacing(1.5)} 0 0 ${theme.spacing(1.5)}`,
    backgroundColor: theme.palette.surface.silver,
    pointerEvents: noPointer ? "none" : "auto",
    width: "100%",
  },

  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
    borderRadius: `${theme.spacing(1.5)} 0 0 ${theme.spacing(1.5)}`,
    outline: "none",
  },

  "& .MuiSelect-icon": {
    color: theme.palette.text.secondary,
    fontSize: "24px",
  },

  "&:hover": {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
}));

const SendTokenDrawer = ({
  isOpen,
  isLoading,
  isSending,
  isOpenConfirmDrawer,
  close,
  indexedData,
  selectedTokenIndex,
  handleMaxAmount,
  handleOpenSearchDrawer,
  handleSelectTokenIndex,
  amount,
  amountRef,
  address,
  handleEnterAddress,
  handleEnterMemo,
  handleOpenConfirmDrawer,
  handleCloseConfirmDrawer,
  isContinueDisabled,
  isOpenSearchDrawer,
  handleCloseSearchDrawer,
  closeSearchDrawer,
  handleConfirm,
  handleOnInputChange,
  handleOnBlur,
  setAmount,
}: {
  isOpen: boolean;
  isLoading: boolean;
  isSending: boolean;
  isOpenConfirmDrawer: boolean;
  close: () => void;
  indexedData: any[];
  selectedTokenIndex: number;
  handleMaxAmount: () => void;
  handleOpenSearchDrawer: () => void;
  handleSelectTokenIndex: (index: number) => void;
  amount: number;
  amountRef: React.RefObject<HTMLInputElement>;
  address: string;
  handleEnterAddress: (address: string) => void;
  handleEnterMemo: (memo: string) => void;
  handleOpenConfirmDrawer: () => void;
  handleCloseConfirmDrawer: () => void;
  isContinueDisabled: boolean;
  isOpenSearchDrawer: boolean;
  handleCloseSearchDrawer: () => void;
  closeSearchDrawer: (token: any) => void;
  handleConfirm: () => void;
  handleOnInputChange: () => void;
  handleOnBlur: () => void;
  setAmount: (value: number) => void;
}) => {
  const theme = useTheme();

  return (
    <>
      <Drawer
        sx={{
          "& .MuiDrawer-paper": {
            width: "100%",
            height: "100%",
            backgroundColor: theme.palette.surface.black,
            color: theme.palette.text.primary,
            display: "flex",
            justifyContent: "start",
            alignItems: "center",
            padding: "24px",
          },
        }}
        open={isOpen && !isLoading}
        anchor="bottom">
        <Col
          sx={{
            background: "url('/send-token-drawer-bg.png') no-repeat center center",
            backgroundSize: "contain",
          }}
          fullWidth
          fullHeight>
          <Col
            type="cc"
            sx={{
              backdropFilter: "blur(3px)",
              background: `
                linear-gradient(
                  0deg,
                  rgba(0, 0, 0, 1) 0%,
                  rgba(0, 0, 0, 1) 20%,
                  rgba(0, 0, 0, 0.1) 100%
                ),
                linear-gradient(
                  90deg,
                  rgba(0, 0, 0, 1) 0%,
                  rgba(0, 0, 0, 0.1) 50%
                ),
                linear-gradient(
                  270deg,
                  rgba(0, 0, 0, 1) 0%,
                  rgba(0, 0, 0, 0.1) 50%
                )
              `,
            }}
            fullWidth
            fullHeight>
            <Row type="ec" sx={{ gap: "8px", maxWidth: "680px" }} fullWidth>
              <CloseButton onClick={close} />
            </Row>
            <Col
              type="bc"
              sx={{ padding: "72px 16px 16px", maxWidth: "680px" }}
              fullWidth
              fullHeight>
              <Col type="cc" gap="8px">
                <img src={CabalTownLogo} alt="cabal-town-logo" height={25} />
                <Typography variant="mobile-h2">Send Tokens</Typography>
              </Col>
              <Col type="bc" gap="16px" fullWidth>
                <Row type="bc" fullWidth>
                  <Row type="sc" sx={{ gap: "4px" }} fullWidth>
                    <Typography variant="mobile-body-m-heavy">Balance:</Typography>
                    {NATIVE_CURRENCY_CHECK_LIST.includes(
                      indexedData[selectedTokenIndex]?.symbol,
                    ) ? (
                      <>
                        <Value
                          variant="mobile-body-m-heavy"
                          color="text.secondary"
                          value={indexedData[selectedTokenIndex].priceUsd}
                          smallNumberSignificantDigits={2}
                          isPrice
                        />{" "}
                        <Typography variant="mobile-body-m-heavy" color="text.secondary">
                          USD
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Value
                          variant="mobile-body-m-heavy"
                          color="text.secondary"
                          value={indexedData[selectedTokenIndex]?.priceUsd}
                          smallNumberSignificantDigits={2}
                          isPrice
                        />{" "}
                        <Typography variant="mobile-body-m-heavy" color="text.secondary">
                          USD
                        </Typography>
                      </>
                    )}
                  </Row>
                  <BasicButton variant="disabled" onClick={handleMaxAmount}>
                    <Typography variant="mobile-action-s-heavy">Max</Typography>
                  </BasicButton>
                </Row>
                <Col type="bc" sx={{ gap: "24px" }} fullWidth>
                  <Col type="cc" sx={{ gap: "8px" }} fullWidth>
                    <Row
                      type="cc"
                      sx={{
                        backgroundColor: "transparent",
                        border: `1px solid ${theme.palette.border.light}`,
                        borderRadius: "16px",
                        outline: "none",
                        paddingY: "12px",
                        paddingX: "16px",
                        height: "45px",
                        cursor: "pointer",
                      }}
                      fullWidth
                      onClick={handleOpenSearchDrawer}>
                      <Row type="cc" sx={{ gap: "8px" }}>
                        <CoinAvatar
                          style={{ width: "24px", height: "24px" }}
                          iconUri={
                            NATIVE_CURRENCY_CHECK_LIST.includes(
                              indexedData[selectedTokenIndex]?.symbol,
                            )
                              ? NATIVE_TOKEN_MAP[
                                  indexedData[selectedTokenIndex]?.symbol.toUpperCase()
                                ]
                              : indexedData[selectedTokenIndex]?.imageUrl
                          }
                          chain={indexedData[selectedTokenIndex]?.chain}
                        />
                        <Typography variant="mobile-body-s-heavy" color="text.primary">
                          {indexedData[selectedTokenIndex]?.symbol}
                        </Typography>
                      </Row>
                      <ArrowDropDown />
                    </Row>
                    <CurrencyInput
                      amount={amount}
                      amountRef={amountRef}
                      onInputChange={handleOnInputChange}
                    />
                  </Col>
                  <Typography variant="mobile-body-s-heavy" color="text.secondary">
                    To
                  </Typography>
                  <TransparentInput
                    placeholder="Enter Address"
                    onChange={(event) => handleEnterAddress(event.target.value)}
                  />
                  {indexedData &&
                  indexedData[selectedTokenIndex] &&
                  indexedData[selectedTokenIndex].chain === TON ? (
                    <>
                      <Typography variant="mobile-body-s-heavy" color="text.secondary">
                        Memo (Optional)
                      </Typography>
                      <TransparentInput
                        placeholder="Enter memo"
                        onChange={(event) => handleEnterMemo(event.target.value)}
                      />
                    </>
                  ) : null}
                </Col>
                <Row type="cc" sx={{ gap: "8px" }} fullWidth>
                  <BasicButton variant="white" sx={{ width: "100%" }} onClick={close}>
                    <Typography variant="mobile-action-s-heavy">Back</Typography>
                  </BasicButton>
                  <BasicButton
                    variant="primary"
                    sx={{ width: "100%" }}
                    onClick={handleOpenConfirmDrawer}
                    disabled={isContinueDisabled}>
                    <Typography variant="mobile-action-s-heavy">Continue</Typography>
                  </BasicButton>
                </Row>
              </Col>
            </Col>
          </Col>
        </Col>
      </Drawer>

      {/* Search */}
      <Drawer
        sx={{
          "& .MuiDrawer-paper": {
            width: "100%",
            height: "100%",
            backgroundColor: theme.palette.surface.black,
            color: theme.palette.text.primary,
            display: "flex",
            justifyContent: "start",
            alignItems: "center",
            padding: "0 0px 24px",
          },
        }}
        open={isOpenSearchDrawer}
        anchor="bottom"
        onClose={handleCloseSearchDrawer}>
        <Col
          type="bc"
          sx={{ position: "relative", maxWidth: "1200px", margin: "0 auto" }}
          fullWidth>
          <Row
            type="bc"
            sx={{
              position: "sticky",
              top: "0",
              left: "0",
              padding: "24px 0",
              backgroundColor: theme.palette.surface.black,
              width: "100%",
              zIndex: 100,
            }}>
            <Box sx={{ width: "28px" }} />
            <Typography variant="mobile-h2">Select Token</Typography>
            <CloseButton onClick={handleCloseSearchDrawer} />
          </Row>
          <Col
            type="cc"
            sx={{
              overflowY: "auto",
              maxHeight: "100%",
              alignItems: "center",
              gap: "8px",
            }}
            fullWidth>
            {indexedData.map((token) => {
              const priceUsd = NATIVE_CURRENCY_CHECK_LIST.includes(token?.symbol)
                ? token?.priceUsd - (minimumGasBufferUSD[token.chain as Chain] ?? 0)
                : token?.priceUsd;

              if (priceUsd < 0) {
                return null;
              }

              return (
                <Row
                  key={token.index}
                  type="bc"
                  sx={{
                    borderRadius: "16px",
                    border: `1px solid ${theme.palette.border.dark}`,
                    background: theme.palette.surface["container-bg"],
                    padding: "12px",
                  }}
                  onClick={() => closeSearchDrawer(token)}
                  fullWidth>
                  <Row
                    type="bc"
                    sx={{
                      backgroundColor: "transparent",
                      gap: "16px",
                    }}>
                    <CoinAvatar
                      style={{
                        width: "28px",
                        height: "28px",
                      }}
                      badgeStyle={{
                        width: "14px",
                        height: "14px",
                      }}
                      iconUri={token.imageUrl ? token.imageUrl : NATIVE_TOKEN_MAP[token.symbol]}
                      chain={token.chain}
                      size="sm"
                    />
                    <Typography variant="mobile-body-s-heavy">{token.symbol}</Typography>
                  </Row>
                  <Value variant="mobile-body-s-heavy" value={priceUsd} isPrice />
                </Row>
              );
            })}
          </Col>
        </Col>
      </Drawer>
      <ConfirmDrawer
        mode={"SEND"}
        isOpen={isOpenConfirmDrawer}
        inputUsd={amount}
        targetTokenPrice={
          indexedData[selectedTokenIndex]?.priceUsd / indexedData[selectedTokenIndex]?.balance
        }
        targetAmount={
          amount /
          (indexedData[selectedTokenIndex]?.priceUsd / indexedData[selectedTokenIndex]?.balance)
        }
        targetTokenSymbol={indexedData[selectedTokenIndex]?.symbol}
        close={handleCloseConfirmDrawer}
        onConfirm={handleConfirm}
      />
    </>
  );
};

const CurrencyInput = ({
  amount,
  amountRef,
  onInputChange,
}: {
  amount: number;
  amountRef: React.RefObject<HTMLInputElement>;
  onInputChange: () => void;
}) => {
  const theme = useTheme();
  const measureRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (amountRef.current && measureRef.current) {
      measureRef.current.style.font = getComputedStyle(amountRef.current).font;
      measureRef.current.textContent = amountRef.current.value || "0"; // Default to "0" if empty
      amountRef.current.style.width = `${measureRef.current.offsetWidth + 10}px`;
    }
  }, [amount]);

  return (
    <Row
      type="cc"
      onClick={() => {
        amountRef.current?.focus();
      }}
      sx={{
        border: `1px solid ${theme.palette.border.light}`,
        height: "45px",
        paddingY: "12px",
        borderRadius: "16px",
      }}
      fullWidth>
      <TransparentInput
        type="number"
        ref={amountRef}
        sx={{
          width: "fit-content",
          display: "inline-block",
          padding: "4px",
          minWidth: "0",
          boxSizing: "border-box",
          border: "none",
        }}
        placeholder="0"
        onChange={() => {
          onInputChange();
        }}
      />
      <span
        ref={measureRef}
        id="measure"
        style={{ visibility: "hidden", position: "absolute", whiteSpace: "pre" }}></span>
      <Typography variant="mobile-body-s-heavy" color="text.primary">
        USD
      </Typography>
    </Row>
  );
};

const TransparentInput = styled("input")(({ theme }) => ({
  all: "unset",
  backgroundColor: "transparent",
  border: `1px solid ${theme.palette.border.light}`,
  borderRadius: "16px",
  outline: "none",
  paddingY: "12px",
  width: "100%",
  height: "45px",
  textAlign: "center",
  ...theme.typography["mobile-body-s"],
  "&:focus, &:not(:placeholder-shown)": {
    opacity: "1",
    outline: "none",
  },
  "&::placeholder": {
    color: theme.palette.text.secondary,
    ...theme.typography["mobile-body-s"],
  },
  color: theme.palette.text.primary,
}));

export { SendTokenDrawer };
